import { gridBoundsMm } from './gridGeometry';
import { boxMeetsBounds } from './orientedBox';
import { sliceGeometryMismatch } from '~/domain/services/sliceStackFrame';
import {
    ReformatExtension,
    ReformatRegion,
    buildReformatGrid,
    extensionSliceCount,
    regionSliceCount,
} from './reformatRegion';
import { MAX_OUTPUT_VOXELS, MIN_SLICES } from './reformatValidation';
import {
    ReformatBlockedReason,
    ReformatTarget,
    reformatBlockedReason,
    reformatSourceBoxMm,
    reformatSourceSlices,
} from './reformatTarget';

/**
 * Why the reformat as currently configured cannot proceed, or null.
 *
 * The ONE predicate for it. The dialog calls it to disable its start
 * control and name the reason; `executeReformat` calls it to refuse. Two
 * hand-written copies had already drifted onto different criteria for the
 * same condition, so a source the panel accepted could fail mid-run.
 *
 * Ordered by what the user can act on first: a target that can never be
 * reformatted before a region that merely needs moving.
 */
export function reformatRegionRefusal(
    target: ReformatTarget,
    region: ReformatRegion,
    matrixSize: number,
    sliceThicknessMm: number,
    extension: ReformatExtension
): ReformatBlockedReason | null {
    const blocked = reformatBlockedReason(target);
    if (blocked !== null) {
        return blocked;
    }

    // Slices that do not share one geometry have no volume to resample.
    // The same test the assembler raises on, so a series it would throw
    // halfway through is named here instead — which matters because the
    // reformat is offered precisely for the series too irregular to load,
    // and a three-plane localizer under one Series Instance UID is one.
    if (sliceGeometryMismatch(reformatSourceSlices(target)) !== null) {
        return 'mixedGeometry';
    }

    // Replicating a boundary slice needs a boundary to replicate: a single
    // acquired slice extruded into a prism is not scattering material, it
    // is one slice claimed to be a volume.
    const replicates =
        extensionSliceCount(extension, sliceThicknessMm) > 0 &&
        (extension.superior || extension.inferior);
    if (replicates && reformatSourceSlices(target).length < 2) {
        return 'extensionNeedsMultipleSlices';
    }

    // Tested on the selected region, not the extended output: an extension
    // reaching past the source is expected, a region that never touches it
    // is not. Against the source's own parallelepiped rather than the box
    // around it, so a region that only meets an oblique source's empty
    // corner is refused rather than accepted as overlapping.
    const meets = boxMeetsBounds(
        reformatSourceBoxMm(target),
        gridBoundsMm(buildReformatGrid(region, matrixSize, sliceThicknessMm))
    );
    if (!meets) {
        return 'regionOutsideSource';
    }

    // The slice range must still hold a volume. The setters refuse a drag
    // that would collapse it, but the region also moves when the slice
    // thickness changes, and a range that was three slices at 1 mm is one
    // slice at 5 mm. Checked here so both the panel and `executeReformat`
    // see the same collapse.
    if (regionSliceCount(region, sliceThicknessMm) < MIN_SLICES) {
        return 'tooFewSlices';
    }

    // Counted on the EXTENDED output, which is what is allocated.
    const voxels =
        matrixSize *
        matrixSize *
        (regionSliceCount(region, sliceThicknessMm) +
            extensionSliceCount(extension, sliceThicknessMm) *
                ((extension.superior ? 1 : 0) + (extension.inferior ? 1 : 0)));
    if (voxels > MAX_OUTPUT_VOXELS) {
        return 'outputTooLarge';
    }

    return null;
}
